import React, { useCallback, useEffect, useMemo, useState } from "react";
import PropTypes from "prop-types";
import { Plus } from "lucide-react";
import { useDispatch, useSelector } from "react-redux";
import { useHistory, useParams } from "react-router-dom";

import Breadcrumbs from "../../../components/BreadCrumbs";
import Filters from "../../../components/Filter";
import DateRangeFilterInput from "../../../components/Filter/DateRangeFilterInput";
import AddReportModal from "../../../components/Events/Event/Reports/AddReportModal";
import ReportsEmptyState from "../../../components/Events/Event/Reports/ReportsEmptyState";
import ReportsTable from "../../../components/Events/Event/Reports/ReportsTable";
import {
  buildCompanyReportsListParams,
  buildReportExportParams,
  EMPTY_REPORT_EXPORT_FIELDS,
  getReporterPositionFilterOptions,
  getReportExportFields,
  joinSelectedReportExportColumns,
  mapReportRow,
} from "../../../components/Events/Event/Reports/reportMappers";
import { buildIssueImagesAttributes } from "../../../components/Events/Event/Reports/reportUpload";
import {
  UIButton,
  UIExportModal,
  UIIcon,
  UIPaginationBar,
} from "../../../components/ui";
import messages from "../../../assets/locale/messages";
import useDebounce from "../../../hooks/useDebounce";
import { adminPermissionModules } from "../../../utils/Constants";
import { useAdminModulePermission } from "../../../utils/adminPortalHelpers";
import { ROUTE_PATHS } from "../../../utils/RoutesPaths";
import {
  createReportRequest,
  exportReportSheetRequest,
  getReportsRequest,
} from "../../../store/Reports/actions";
import {
  setReportPage,
  setReportRow,
} from "../../../store/PersistReport/actions";

const SEARCH_DEBOUNCE_MS = 300;

function Reports({ embedded }) {
  const { id } = useParams();
  const history = useHistory();
  const dispatch = useDispatch();
  const lang = useSelector((state) => state.locale.lang);
  const { report, shared } = messages[lang];
  const isLoading = useSelector((state) => state.loader);
  const list = useSelector((state) => state.report.list);
  const meta = useSelector((state) => state.report.meta);
  const creating = useSelector((state) => state.report.creating);
  const event = useSelector((state) => state.events.event);
  const persistState = useSelector((state) => state.persistReport);
  const { canAdd } = useAdminModulePermission(
    adminPermissionModules.manageReports,
  );

  const [showExportModal, setShowExportModal] = useState(false);
  const [showAddModal, setShowAddModal] = useState(false);
  const [selectedFields, setSelectedFields] = useState(EMPTY_REPORT_EXPORT_FIELDS);
  const [searchValue, setSearchValue] = useState("");
  const debouncedSearch = useDebounce(searchValue, SEARCH_DEBOUNCE_MS);
  const [zoneFilter, setZoneFilter] = useState(null);
  const [reporterPositionFilter, setReporterPositionFilter] = useState(null);
  const [reportedAtFilter, setReportedAtFilter] = useState(null);
  const [resolvedAtFilter, setResolvedAtFilter] = useState(null);
  const [page, setPage] = useState(persistState.page || 1);

  const pageSize = persistState.rowCount || 10;
  const totalCount = meta.count || 0;
  const paginationPages =
    meta.pages || Math.max(Math.ceil(totalCount / pageSize), 1);
  const paginationFrom = totalCount > 0 ? (page - 1) * pageSize + 1 : 0;
  const paginationTo = Math.min(page * pageSize, totalCount);

  const zoneOptions = useMemo(() => {
    const zones = event?.zones_attributes || event?.zones || [];
    return (Array.isArray(zones) ? zones : []).map((zone) => ({
      id: zone.id,
      name: zone.name || zone.attributes?.name || String(zone.id),
    }));
  }, [event]);

  const reporterPositionOptions = useMemo(
    () => getReporterPositionFilterOptions(report.reporterPositions),
    [report.reporterPositions],
  );

  const listParams = useMemo(
    () =>
      buildCompanyReportsListParams({
        search: debouncedSearch,
        page,
        pageSize,
        zoneId: zoneFilter?.id,
        issuerType: reporterPositionFilter?.id,
        reportedAt: reportedAtFilter,
        resolvedAt: resolvedAtFilter,
      }),
    [
      debouncedSearch,
      page,
      pageSize,
      zoneFilter,
      reporterPositionFilter,
      reportedAtFilter,
      resolvedAtFilter,
    ],
  );

  const rows = useMemo(
    () =>
      (Array.isArray(list) ? list : []).map((issue) =>
        mapReportRow(issue, report.reporterPositions),
      ),
    [list, report.reporterPositions],
  );

  const exportFields = useMemo(
    () => getReportExportFields(report.exportSheetColumns, report.updates),
    [report.exportSheetColumns, report.updates],
  );

  const selectedExportCount = Object.values(selectedFields).filter(Boolean).length;
  const isAllExportSelected =
    exportFields.length > 0 && selectedExportCount === exportFields.length;
  const isExportIndeterminate =
    selectedExportCount > 0 && selectedExportCount < exportFields.length;

  const tableLabels = useMemo(
    () => ({
      reportTitle: report.listReport.reportTitle,
      zone: report.listReport.zones,
      name: report.listReport.name,
      role: report.listReport.role,
      createdDateTime: report.listReport.createdDateTime,
      closedDateTime: report.listReport.closedDateTime,
      dateTime: report.listReport.dateTime,
      status: report.listReport.status,
      open: report.listReport.open,
      closed: report.listReport.closed,
    }),
    [report.listReport],
  );

  const addModalLabels = useMemo(
    () => ({
      title: report.addReport.title,
      description: report.addReport.description,
      cancel: shared.cancel,
      submit: report.addReport.submit,
      reportTitle: report.addReport.reportTitle,
      reportTitlePlaceholder: report.addReport.reportTitlePlaceholder,
      zone: report.addReport.zone,
      zonePlaceholder: report.addReport.zonePlaceholder,
      issueDescription: report.addReport.issueDescription,
      issueDescriptionPlaceholder: report.addReport.issueDescriptionPlaceholder,
      descriptionHint: report.addReport.descriptionHint,
      descriptionMaxLengthError: report.addReport.descriptionMaxLengthError,
      open: report.listReport.open,
      closed: report.listReport.closed,
      reportStatus: report.addReport.reportStatus,
      uploadLabel: report.addReport.uploadLabel,
      uploadClickToUpload: report.addReport.uploadClickToUpload,
      uploadDragHint: report.addReport.uploadDragHint,
      uploadHint: report.addReport.uploadHint,
      uploadFormatHint: report.addReport.uploadFormatHint,
      uploadRemainingHint: report.addReport.uploadRemainingHint,
      uploadLimitReached: report.addReport.uploadLimitReached,
      uploadMaxSizeError: report.addReport.uploadMaxSizeError,
      uploadTypeError: report.addReport.uploadTypeError,
      required: report.addReport.required,
    }),
    [report, shared.cancel],
  );

  const handleCloseExportModal = () => {
    setShowExportModal(false);
    setSelectedFields(EMPTY_REPORT_EXPORT_FIELDS);
  };

  const handleExportFieldChange = (name, checked) => {
    setSelectedFields((prev) => ({ ...prev, [name]: checked }));
  };

  const handleSelectAllExport = (checked) => {
    setSelectedFields((prev) => {
      const next = { ...prev };
      exportFields.forEach((field) => {
        next[field.name] = checked;
      });
      return next;
    });
  };

  const handleExportSheet = () => {
    if (!id) return;

    const columns = joinSelectedReportExportColumns(
      exportFields,
      selectedFields,
    );
    if (!columns) return;

    dispatch(
      exportReportSheetRequest({
        id,
        params: buildReportExportParams({ columns, listParams }),
      }),
    );
    handleCloseExportModal();
  };

  const handlePageChange = (value) => {
    setPage(value);
    dispatch(setReportPage(value));
  };

  const handlePageSizeChange = (value) => {
    dispatch(setReportRow(value));
    setPage(1);
    dispatch(setReportPage(1));
  };

  const goToFirstPage = () => {
    setPage(1);
    dispatch(setReportPage(1));
  };

  useEffect(() => {
    if (!id) return;
    dispatch(
      getReportsRequest({
        id,
        params: listParams,
      }),
    );
  }, [dispatch, id, listParams, lang]);

  const hasActiveFilters = Boolean(
    debouncedSearch.trim() ||
      zoneFilter ||
      reporterPositionFilter ||
      reportedAtFilter ||
      resolvedAtFilter,
  );

  const handleCreateReport = useCallback(
    (formValues) => {
      if (!id) return;
      const imageUrls =
        formValues.imageUrls ||
        (formValues.imageUrl ? [formValues.imageUrl] : []);
      const data = {
        zone_id: formValues.zoneId,
        title: formValues.title,
        description: formValues.description,
        status: formValues.status,
      };
      const images = buildIssueImagesAttributes(imageUrls);
      if (images.length) {
        data.logistics_issue_images_attributes = images;
      }

      dispatch(
        createReportRequest({
          id,
          data,
          onSuccess: () => {
            setShowAddModal(false);
            dispatch(
              getReportsRequest({
                id,
                params: listParams,
              }),
            );
          },
        }),
      );
    },
    [dispatch, id, listParams],
  );

  const renderContent = () => {
    if (isLoading) return null;

    if (!rows.length) {
      return (
        <ReportsEmptyState
          title={hasActiveFilters ? report.noResultsTitle : report.emptyTitle}
          description={
            hasActiveFilters
              ? report.noResultsDescription
              : report.eventEmptyDescription
          }
          actionLabel={report.addReport.addNewReport}
          onAction={() => setShowAddModal(true)}
          showAction={!hasActiveFilters && canAdd}
        />
      );
    }

    return (
      <>
        <ReportsTable
          rows={rows}
          labels={tableLabels}
          pageSize={pageSize}
          variant="event"
          onRowClick={(row) => {
            if (!row?.id) return;
            history.push(
              ROUTE_PATHS.viewReportDetail
                .replace(":event_id", id)
                .replace(":id", row.id),
            );
          }}
        />
        {totalCount > 0 ? (
          <UIPaginationBar
            currentPage={page}
            pages={paginationPages}
            pageSize={pageSize}
            rangeTemplate={report.showingRange}
            rangeFrom={paginationFrom}
            rangeTo={paginationTo}
            rangeTotal={totalCount}
            rowsPerPageLabel={shared.numberOfRows}
            onPageChange={handlePageChange}
            onPageSizeChange={handlePageSizeChange}
            className="reports-list-pagination"
          />
        ) : null}
      </>
    );
  };

  return (
    <div className={embedded ? "reports-list reports-list--embedded" : "reports-list"}>
      {!embedded && (
        <div className="w-100 page-title-margin">
          <Breadcrumbs
            list={[
              {
                path: `${ROUTE_PATHS.viewEvent.replace(":id", id)}?tab=reports`,
                label: shared.listBreadCrumbs.listReportsBreadcrumb,
              },
              {
                path: "",
                label: report.listIssuesBreadcrumb,
              },
            ]}
            BreadCrumbHasBackIcon={false}
          />
        </div>
      )}

      <Filters
        isShowSearch
        isCollapsable
        renderActionBeforeFilterToggle={
          <>
            <UIButton
              type="button"
              variant="primary"
              appearance="outline"
              size="large"
              startIcon={
                <UIIcon
                  name="exportSheet"
                  width={18}
                  height={18}
                  color="currentColor"
                  strokeWidth={1.5}
                  decorative
                />
              }
              onClick={() => setShowExportModal(true)}
            >
              {report.exportSheetColumns.exportReportToExcel}
            </UIButton>
            {canAdd && (
              <UIButton
                type="button"
                variant="primary"
                appearance="outline"
                size="large"
                startIcon={<Plus size={16} aria-hidden />}
                onClick={() => setShowAddModal(true)}
              >
                {report.addReport.addNewReport}
              </UIButton>
            )}
          </>
        }
        searchInput={{
          placeholder: report.searchPlaceholder,
          name: "search",
          value: searchValue,
          onChange: (value) => {
            setSearchValue(value);
            goToFirstPage();
          },
        }}
        filters={[
          {
            type: "filter",
            name: "zone",
            placeholder: report.listReport.zone,
            value: zoneFilter,
            options: zoneOptions,
            onChange: (value) => {
              setZoneFilter(value);
              goToFirstPage();
            },
          },
          {
            type: "filter",
            name: "reporterPosition",
            placeholder: report.reporterPosition,
            value: reporterPositionFilter,
            options: reporterPositionOptions,
            onChange: (value) => {
              setReporterPositionFilter(value);
              goToFirstPage();
            },
          },
          {
            type: "component",
            name: "reportedAt",
            className: "col-sm-12 col-md-6 col-lg-3",
            render: () => (
              <DateRangeFilterInput
                name="reportedAt"
                placeholder={report.reportedAt}
                labels={report.dateRangeFilter}
                value={reportedAtFilter}
                onChange={(value) => {
                  setReportedAtFilter(value);
                  goToFirstPage();
                }}
              />
            ),
          },
          {
            type: "component",
            name: "resolvedAt",
            className: "col-sm-12 col-md-6 col-lg-3",
            render: () => (
              <DateRangeFilterInput
                name="resolvedAt"
                placeholder={report.resolvedAt}
                labels={report.dateRangeFilter}
                value={resolvedAtFilter}
                onChange={(value) => {
                  setResolvedAtFilter(value);
                  goToFirstPage();
                }}
              />
            ),
          },
        ]}
        classNames={{
          container: "reports-list-filters",
        }}
      />

      {renderContent()}

      <UIExportModal
        open={showExportModal}
        title={report.modalTitle}
        description={report.exportModalDescription}
        selectAllLabel={report.selectAllFields}
        cancelLabel={shared.cancel}
        confirmLabel={report.exportSheetColumns.export}
        fields={exportFields}
        selectedFields={selectedFields}
        isAllSelected={isAllExportSelected}
        isIndeterminate={isExportIndeterminate}
        confirmDisabled={selectedExportCount === 0}
        onClose={handleCloseExportModal}
        onCancel={handleCloseExportModal}
        onConfirm={handleExportSheet}
        onFieldChange={handleExportFieldChange}
        onSelectAll={handleSelectAllExport}
        maxWidth="xs"
      />

      <AddReportModal
        open={showAddModal}
        labels={addModalLabels}
        zoneOptions={zoneOptions}
        submitting={creating}
        onClose={() => setShowAddModal(false)}
        onSubmit={handleCreateReport}
      />
    </div>
  );
}

Reports.propTypes = {
  embedded: PropTypes.bool,
};

Reports.defaultProps = {
  embedded: false,
};

export default Reports;
